import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Orbit, Loader2, Sun, Moon, Star, ArrowUp, 
  AlertTriangle, CheckCircle, RefreshCw, Sparkles, Crown, Lock
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Link } from "react-router-dom";

const PLANET_ICONS = {
  Sun: "☉",
  Moon: "☽",
  Mercury: "☿",
  Venus: "♀",
  Mars: "♂",
  Jupiter: "♃",
  Saturn: "♄",
  Uranus: "♅",
  Neptune: "♆",
  Pluto: "♇"
};

const Transits = () => {
  const { token } = useAuth();
  const [transits, setTransits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [premiumRequired, setPremiumRequired] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchTransits();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTransits = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/astrology/transits`, { headers, withCredentials: true });
      
      if (res.data.premium_required) {
        setPremiumRequired(res.data);
        setTransits(null);
      } else {
        setTransits(res.data);
        setPremiumRequired(null);
      }
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
        <p className="text-mystic-200/70">Calculating planetary positions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transits-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Orbit className="w-8 h-8 text-indigo-400" />
              Planetary Transits
            </h1>
            <p className="text-mystic-200/70">
              How current planetary movements affect your birth chart
            </p>
          </div>
          {!premiumRequired && (
            <Button
              onClick={() => fetchTransits(true)}
              disabled={refreshing}
              variant="outline"
              className="border-mystic-500/50 text-mystic-300 hover:bg-mystic-500/20"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          )}
        </div>
      </motion.div>

      {/* Premium Required */}
      {premiumRequired && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="glass border-moon-gold/30 bg-gradient-to-br from-moon-gold/10 to-amber-500/5">
            <CardContent className="py-12 text-center">
              <Lock className="w-16 h-16 text-moon-gold mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Premium Feature</h2>
              <p className="text-mystic-200/70 mb-4 max-w-md mx-auto">
                {premiumRequired.message}
              </p>
              <p className="text-mystic-200/70 mb-6 max-w-md mx-auto text-sm">
                {premiumRequired.preview?.sample_insight}
              </p>
              
              {/* Show current sky preview */}
              {premiumRequired.preview?.current_sky && (
                <div className="glass-light rounded-xl p-4 mb-6 max-w-lg mx-auto">
                  <p className="text-mystic-300 text-sm mb-3">Current planetary positions (preview):</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {Object.entries(premiumRequired.preview.current_sky).slice(0, 4).map(([planet, data]) => (
                      <Badge key={planet} variant="outline" className="border-mystic-500/30 text-mystic-200">
                        {PLANET_ICONS[planet]} {planet} in {data.sign}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <Link to="/settings">
                <Button className="bg-moon-gold hover:bg-moon-gold/90 text-mystic-900">
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* No Birth Chart Message */}
      {transits?.message && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass border-amber-500/30">
            <CardContent className="py-8 text-center">
              <Star className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">Birth Chart Required</h3>
              <p className="text-mystic-200/70 mb-4">{transits.message}</p>
              <Link to="/astrology">
                <Button className="bg-mystic-500 hover:bg-mystic-400 text-white rounded-full">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Set Up Birth Chart
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Natal Signs */}
      {transits?.natal_signs && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <Card className="glass bg-gradient-to-br from-amber-500/10 to-transparent">
            <CardContent className="py-4 text-center">
              <Sun className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-mystic-300 text-sm">Your Sun</p>
              <p className="text-white font-semibold text-lg">{transits.natal_signs.sun}</p>
            </CardContent>
          </Card>
          <Card className="glass bg-gradient-to-br from-indigo-500/10 to-transparent">
            <CardContent className="py-4 text-center">
              <Moon className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
              <p className="text-mystic-300 text-sm">Your Moon</p>
              <p className="text-white font-semibold text-lg">{transits.natal_signs.moon}</p>
            </CardContent>
          </Card>
          <Card className="glass bg-gradient-to-br from-pink-500/10 to-transparent">
            <CardContent className="py-4 text-center">
              <ArrowUp className="w-6 h-6 text-pink-400 mx-auto mb-2" />
              <p className="text-mystic-300 text-sm">Your Rising</p>
              <p className="text-white font-semibold text-lg">{transits.natal_signs.rising}</p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Current Sky */}
      {transits?.current_sky && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-moon-gold" />
                Current Planetary Positions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(transits.current_sky).map(([planet, data]) => (
                  <div key={planet} className="glass-light rounded-lg p-3 text-center">
                    <span className="text-2xl block mb-1">{PLANET_ICONS[planet]}</span>
                    <p className="text-white font-medium text-sm">{planet}</p>
                    <p className="text-mystic-300 text-xs">{data.sign}</p>
                    <p className="text-mystic-400 text-xs">{data.degree?.toFixed(1)}°</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Active Transits */}
      {transits?.transits && transits.transits.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Orbit className="w-5 h-5 text-indigo-400" />
                Active Transits to Your Chart
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {transits.transits.map((transit, idx) => (
                <motion.div
                  key={`k-${idx}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`glass-light rounded-xl p-4 border-l-4 ${
                    transit.harmony === 'positive' ? 'border-green-500' :
                    transit.harmony === 'challenging' ? 'border-amber-500' :
                    'border-mystic-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{PLANET_ICONS[transit.transit_planet]}</span>
                      <div>
                        <p className="text-white font-medium">{transit.meaning}</p>
                        <p className="text-mystic-300 text-sm">
                          {transit.transit_planet} in {transit.transit_sign} ({transit.transit_degree}°) 
                          <span className="mx-2">{transit.aspect_symbol}</span>
                          Natal {transit.natal_point}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`${
                        transit.harmony === 'positive' ? 'border-green-500/50 text-green-400' :
                        transit.harmony === 'challenging' ? 'border-amber-500/50 text-amber-400' :
                        'border-mystic-500/50 text-mystic-300'
                      }`}
                    >
                      {transit.harmony === 'positive' ? (
                        <CheckCircle className="w-3 h-3 mr-1" />
                      ) : transit.harmony === 'challenging' ? (
                        <AlertTriangle className="w-3 h-3 mr-1" />
                      ) : null}
                      {transit.aspect}
                    </Badge>
                  </div>
                  <p className="text-mystic-200/80 text-sm mb-2">{transit.interpretation}</p>
                  <p className="text-mystic-400 text-xs italic">{transit.advice}</p>
                </motion.div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* No Active Transits */}
      {transits?.transits && transits.transits.length === 0 && !transits.message && (
        <Card className="glass">
          <CardContent className="py-8 text-center">
            <Orbit className="w-12 h-12 text-mystic-500 mx-auto mb-4" />
            <p className="text-mystic-200/70">No major transits active at this time.</p>
            <p className="text-mystic-400 text-sm mt-2">Check back later for cosmic updates!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Transits;
